import React from 'react';
import { BoletoExistsResponse } from '../services/api/boleto.service';

interface BoletoConfirmationDialogProps {
  isOpen: boolean;
  boletoData: BoletoExistsResponse;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}

const BoletoConfirmationDialog: React.FC<BoletoConfirmationDialogProps> = ({
  isOpen,
  boletoData,
  onConfirm,
  onCancel,
  loading = false
}) => {
  if (!isOpen) return null;

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('pt-BR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'text-green-600 bg-green-100';
      case 'processing':
        return 'text-yellow-600 bg-yellow-100';
      case 'failed':
        return 'text-red-600 bg-red-100';
      case 'expired':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-blue-600 bg-blue-100';
    }
  };

  const getStatusText = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'Pago';
      case 'processing':
        return 'Pendente';
      case 'failed':
        return 'Falhou';
      case 'expired':
        return 'Expirado';
      default:
        return status;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                <span className="text-yellow-600 text-xl">⚠️</span>
              </div>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-gray-900">
                Boleto já existe
              </h3>
              <p className="text-sm text-gray-500">
                Um boleto já foi criado para esta fatura
              </p>
            </div>
          </div>

          {/* Message */}
          <div className="mb-4">
            <p className="text-sm text-gray-700">
              {boletoData.message}
            </p>
          </div>

          {/* Existing Boleto Details */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h4 className="text-sm font-medium text-gray-900 mb-3">
              Detalhes do boleto existente:
            </h4>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Status:</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(boletoData.existing_boleto.transaction_status)}`}>
                  {getStatusText(boletoData.existing_boleto.transaction_status)}
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-600">Valor:</span>
                <span className="font-medium">
                  {formatCurrency(boletoData.existing_boleto.amount, boletoData.existing_boleto.currency)}
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-600">Criado em:</span>
                <span>{formatDate(boletoData.existing_boleto.created_at)}</span>
              </div>
              
              {boletoData.existing_boleto.boleto_duedate && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Vencimento:</span>
                  <span>{formatDate(boletoData.existing_boleto.boleto_duedate)}</span>
                </div>
              )}
              
              {boletoData.existing_boleto.boleto_code && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Código:</span>
                  <span className="font-mono text-xs">
                    {boletoData.existing_boleto.boleto_code}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Suggestion */}
          {boletoData.suggestion && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
              <p className="text-sm text-blue-800">
                💡 {boletoData.suggestion}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex space-x-3">
            <button
              onClick={onCancel}
              disabled={loading}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={onConfirm}
              disabled={loading}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Criando...
                </div>
              ) : (
                'Criar novo boleto'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoletoConfirmationDialog;